import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchPartBalances } from '../../supabase/services';

// The Edge Function caps a request at this many parts.
const MAX_PARTS = 200;

// Priority stock for the given part numbers, refetched whenever the set of parts changes
// or refresh() is called (after a report, or when the operator asks).
// `balances` maps part number -> { available, locations }; a part missing from it has not
// loaded (or isn't in Priority) and shows nothing rather than a misleading 0.
const usePartBalances = (parts) => {
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [failed, setFailed] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const requestIdRef = useRef(0);

  // A stable key, so a new array with the same parts doesn't trigger a refetch.
  const partsKey = parts.join('\n');

  useEffect(() => {
    const list = partsKey ? partsKey.split('\n').slice(0, MAX_PARTS) : [];
    if (list.length === 0) return;

    // Only the latest request may write state; an older one finishing late is dropped.
    const requestId = ++requestIdRef.current;
    setLoading(true);
    fetchPartBalances(list).then((result) => {
      if (requestId !== requestIdRef.current) return;
      setLoading(false);
      setFailed(!result.success);
      if (result.success) {
        setBalances(prev => ({ ...prev, ...result.balances }));
        setFetchedAt(new Date(result.fetchedAt));
      }
    });
  }, [partsKey, refreshToken]);

  const refresh = useCallback(() => setRefreshToken(n => n + 1), []);

  return { balances, loading, fetchedAt, failed, refresh };
};

export default usePartBalances;
